import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Plus, Minus, Sparkles } from "lucide-react";
import { NAV_CATEGORIES, PHOTOS, ARTIST, TOUR_DATES } from "../mock";

const CAT_IMAGES = {
  presentation: PHOTOS.portraitBW,
  ep: PHOTOS.heroRedBlue,
  tournee: PHOTOS.tour,
  merch: PHOTOS.cdImage,
  prestations: PHOTOS.stageRedBlue,
  contact: PHOTOS.portraitSofa,
};

export default function Home() {
  const [time, setTime] = useState("");
  const [expandedKey, setExpandedKey] = useState(null);

  useEffect(() => {
    const tick = () => {
      const d = new Date();
      setTime(
        d.toLocaleTimeString("fr-FR", {
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "Europe/Paris",
        })
      );
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="relative">
      {/* HERO INTRO */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-10 lg:pt-16 pb-8">
        <div className="grid md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-7 reveal-up">
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-neutral-500 mb-6">
              <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span>En tournée — Juin 2026</span>
              <span className="text-neutral-700">•</span>
              <span>Paris {time}</span>
            </div>
            <div className="flex items-start justify-between gap-6">
              <h1 className="title-reveal font-display text-[15vw] md:text-[10vw] lg:text-[8vw] leading-[0.85] tracking-tight uppercase">
                Vince<br />
                <span className="text-neutral-500">Rodi</span>
              </h1>
              <a
                href="https://linkaband.com/vince-rodi?utm_source=badge&utm_campaign=226037"
                target="_blank"
                rel="noreferrer"
                data-testid="linkaband-badge"
                className="inline-flex shrink-0 mt-2 border border-white/10 bg-black/40 backdrop-blur-sm p-2 hover:border-white/40 transition-colors"
                aria-label="Réserver Vince Rodi sur Linkaband"
              >
                <img
                  src="https://linkaband.com/assets/images/validation/reservation-noir.png"
                  alt="Vince Rodi · Réservation Linkaband"
                  width="110"
                  height="110"
                  className="block w-[70px] h-[70px] md:w-[90px] md:h-[90px] lg:w-[110px] lg:h-[110px] invert"
                  loading="lazy"
                />
              </a>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <span className="accent-line-red" />
              <span className="accent-line-blue" />
              <span className="text-[10px] uppercase tracking-[0.35em] text-neutral-500">Pop · Variété française</span>
            </div>
          </div>
          <div className="md:col-span-5 reveal-up" style={{ animationDelay: "120ms" }}>
            {/* SPOTIFY — lecteur compact au-dessus de la photo */}
            <div className="mb-4" data-testid="spotify-section">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#1DB954] animate-pulse" />
                  <span className="text-[9px] uppercase tracking-[0.3em] text-neutral-500">
                    Écouter — Spotify
                  </span>
                </div>
                <a
                  href="https://open.spotify.com/artist/5vjrwc9oDSp6pRqE4wJwsU"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="spotify-open-link"
                  className="text-[9px] uppercase tracking-[0.3em] text-neutral-500 hover:text-white inline-flex items-center gap-1"
                >
                  Ouvrir <ArrowUpRight size={10} />
                </a>
              </div>
              <div className="border border-white/10 overflow-hidden bg-neutral-950">
                <iframe
                  title="Vince Rodi — Spotify"
                  data-testid="spotify-iframe"
                  src="https://open.spotify.com/embed/artist/5vjrwc9oDSp6pRqE4wJwsU?utm_source=generator&theme=0"
                  width="100%"
                  height="152"
                  frameBorder="0"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                  className="block"
                />
              </div>
            </div>
            <div className="aspect-[4/5] overflow-hidden bg-neutral-900 mb-6 relative">
              <img
                src="https://customer-assets-4nw71qhi.emergentagent.net/job_musicien-variete/artifacts/2vw6rhl6_EDBEAAC3-9BBB-4331-8474-88D073E47173.webp"
                alt="Vince Rodi"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-3 left-3 text-[10px] uppercase tracking-[0.3em] text-white/90 bg-black/40 backdrop-blur px-2 py-1 border border-white/10">
                EP — Et Après · 2026
              </div>
            </div>
            <p className="font-serif-accent italic text-neutral-300 text-lg leading-relaxed max-w-md">
              « Auteur-compositeur-interprète du sud de la France. Une pop sincère, des mélodies qui restent, et l'envie de partager. »
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/tournee"
                className="inline-flex items-center gap-2 bg-white text-black hover:bg-neutral-200 transition px-5 py-3 text-xs uppercase tracking-[0.2em]"
              >
                Voir la tournée <ArrowUpRight size={14} />
              </Link>
              <a
                href={ARTIST.linktree}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 border border-white/30 hover:border-white hover:bg-white/5 transition px-5 py-3 text-xs uppercase tracking-[0.2em]"
              >
                Écouter « Et Après » <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY LIST — inspired by the reference mockup */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 mt-6 lg:mt-10">
        <div className="flex items-center justify-between border-t border-white/10 pt-6 mb-2">
          <span className="text-[10px] uppercase tracking-[0.35em] text-neutral-500">
            Index — 06 rubriques
          </span>
          <span className="text-[10px] uppercase tracking-[0.35em] text-neutral-500">
            Survolez → cliquez
          </span>
        </div>

        <ul className="divide-y divide-white/10">
          {NAV_CATEGORIES.map((cat, idx) => {
            const isExpanded = expandedKey === cat.key;
            const content = (
              <div className="cat-row group flex items-center justify-between py-7 md:py-9 lg:py-11 cursor-pointer">
                <div className="flex items-baseline gap-5 md:gap-8">
                  <span className={`font-body text-xs md:text-sm tabular-nums w-10 ${idx % 2 === 0 ? "text-red-500" : "text-blue-600"}`}>
                    0{idx + 1}
                  </span>
                  <h2 className="cat-label font-display uppercase text-neutral-400 text-[12vw] md:text-[7vw] lg:text-[6vw] leading-[0.9] tracking-tight">
                    {cat.label}
                  </h2>
                </div>
                <div className="flex items-center gap-4 md:gap-6 pr-2">
                  <span className="hidden lg:inline text-[11px] uppercase tracking-[0.3em] text-neutral-500">
                    {cat.subtitle}
                  </span>
                  {cat.external ? (
                    <ArrowUpRight
                      className="cat-arrow text-neutral-400"
                      size={36}
                      strokeWidth={1.25}
                    />
                  ) : cat.expandable ? (
                    isExpanded ? (
                      <Minus className="cat-arrow text-red-500" size={36} strokeWidth={1.25} />
                    ) : (
                      <Plus className="cat-arrow text-neutral-400" size={36} strokeWidth={1.25} />
                    )
                  ) : (
                    <Plus
                      className="cat-arrow text-neutral-400"
                      size={36}
                      strokeWidth={1.25}
                    />
                  )}
                </div>
                <img
                  src={CAT_IMAGES[cat.key]}
                  alt={cat.label}
                  className="cat-image"
                />
              </div>
            );
            return (
              <li key={cat.key} className="relative">
                {cat.external ? (
                  <a href={cat.to} target="_blank" rel="noreferrer" className="block">
                    {content}
                  </a>
                ) : cat.expandable ? (
                  <button
                    type="button"
                    onClick={() => setExpandedKey(isExpanded ? null : cat.key)}
                    data-testid={`cat-toggle-${cat.key}`}
                    className="block w-full text-left"
                    aria-expanded={isExpanded}
                  >
                    {content}
                  </button>
                ) : (
                  <Link to={cat.to} className="block">
                    {content}
                  </Link>
                )}

                {cat.expandable && cat.key === "tournee" && (
                  <div
                    data-testid="tournee-expand-panel"
                    className={`overflow-hidden transition-[max-height,opacity] duration-500 ease-out ${
                      isExpanded ? "max-h-[1400px] opacity-100" : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="pb-8 pl-14 md:pl-24 pr-4 md:pr-10">
                      <div className="flex items-center gap-4 mb-5">
                        <span className="h-px flex-1 bg-white/10" />
                        <div className="text-[10px] uppercase tracking-[0.35em] text-red-500">
                          Tournée 2026
                        </div>
                        <span className="h-px flex-1 bg-white/10" />
                      </div>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {TOUR_DATES.map((d) => (
                          <Link
                            key={d.id}
                            to={`/tournee#date-${d.id}`}
                            data-testid={`tournee-date-${d.id}`}
                            className={`group flex items-center justify-between gap-3 border border-white/10 hover:border-white/40 bg-white/[0.02] hover:bg-white/[0.05] transition-all px-4 py-3 ${
                              d.highlight ? "border-red-500/40 hover:border-red-500" : ""
                            }`}
                          >
                            <div className="flex items-baseline gap-3">
                              <span className="font-display text-2xl leading-none text-white">{d.day}</span>
                              <div className="flex flex-col">
                                <span className="text-[9px] uppercase tracking-[0.25em] text-neutral-500 leading-none">
                                  {d.month}
                                </span>
                                <span className="text-sm text-neutral-100 mt-1">{d.city}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {d.highlight && (
                                <Sparkles size={14} className="text-red-500" />
                              )}
                              <ArrowUpRight size={14} className="text-neutral-500 group-hover:text-white transition-colors" />
                            </div>
                          </Link>
                        ))}
                      </div>
                      <div className="mt-6 flex justify-end">
                        <Link
                          to="/tournee"
                          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-neutral-400 hover:text-white transition-colors"
                        >
                          Voir la page tournée <ArrowUpRight size={12} />
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      {/* MARQUEE */}
      <section className="mt-20 lg:mt-28 border-y border-white/10 py-6 overflow-hidden">
        <div className="marquee-track flex gap-12 whitespace-nowrap">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center gap-12 font-display text-3xl md:text-5xl uppercase text-neutral-300">
              <span>EP « Et Après »</span>
              <span className="text-red-500">•</span>
              <span>Tournée France Juin 2026</span>
              <span className="text-blue-600">•</span>
              <span>Pop · Variété française</span>
              <span className="text-red-500">•</span>
              <span>Voilà Pourquoi — Single</span>
              <span className="text-blue-600">•</span>
              <span>Fête de la Musique — Paris</span>
              <span className="text-red-500">•</span>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED IMAGE BLOCK */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 mt-20 lg:mt-28">
        <div className="grid md:grid-cols-12 gap-6">
          <div className="md:col-span-7 reveal-up">
            <div className="aspect-[4/5] md:aspect-[4/3] overflow-hidden bg-neutral-900">
              <img
                src={PHOTOS.stageRedBlue}
                alt="Vince Rodi en scène"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]"
              />
            </div>
          </div>
          <div className="md:col-span-5 flex flex-col justify-end">
            <div className="text-[10px] uppercase tracking-[0.35em] text-neutral-500 mb-4">
              — Le projet
            </div>
            <h3 className="font-display text-5xl md:text-6xl uppercase leading-[0.95]">
              Une voix, des histoires.
            </h3>
            <p className="mt-5 text-neutral-400 text-sm leading-relaxed">
              Six chansons pour un premier EP. Une réflexion sur les épreuves, la reconstruction, et la manière dont nos relations nous façonnent. Une pop intime et universelle, portée par l'envie d'aller vers l'autre.
            </p>
            <Link
              to="/presentation"
              className="mt-8 inline-flex items-center gap-2 self-start border border-white/30 hover:border-white hover:bg-white hover:text-black transition-all px-5 py-3 text-xs uppercase tracking-[0.2em]"
            >
              Découvrir l'artiste <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <div className="h-24" />
    </div>
  );
}
